import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { isValidToken, setSession } from './utils';
import { useAuthValidator } from 'src/store/AuthValidater';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};
export function organizeMenuItems(menuItems) {
  const menuMap = {};
  menuItems?.forEach((item) => {
    menuMap[item?._id] = { ...item, children: [] };
  });
  const topLevelItems = [];
  menuItems?.forEach((item) => {
    if (item?.parent && menuMap[item?.parent]) {
      menuMap[item?.parent]?.children?.push(menuMap[item._id]);
    } else {
      topLevelItems.push(menuMap[item._id]);
    }
  });
  // Recursive sort function by order
  const sortByOrder = (items) => {
    items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    items.forEach(item => {
      if (item.children?.length) {

        sortByOrder(item.children);
      }
    });
  };

  sortByOrder(topLevelItems);
  return topLevelItems?.map((it) => {
    return {
      ...it,
      children: it.children.map((ite) => ({ ...ite, icon: "" }))
    }
  });
}
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { handleMenus, handlePartner } = useAuthValidator((state) => state)
  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const user = storageAvailable ? localStorage.getItem('user') : '';

      if (accessToken && isValidToken(accessToken) && user) {
        setSession(accessToken, user);

        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (phone, password) => {
    const data = {
      user: phone,
      password,
    };

    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: '/system/users/sign-in',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    };
    const response = await axios.request(config);

    const { payload } = response.data;
    const menusData = payload?.admin_menu.map((it) => {
      return {
        ...it.menu
      }
    })
    handleMenus(organizeMenuItems(menusData))
    handlePartner(payload?.partner)
    localStorage.setItem('partner', payload?.partner?._id)
    setSession(payload?.token, payload?.user);


    dispatch({
      type: 'LOGIN',
      payload: {
        user: payload?.user,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      register,
      logout,
    }),
    [state.isAuthenticated, state.isInitialized, state.user, login, logout, register]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
